import React, { createContext, useContext, useMemo } from 'react';
import { AsyncResource, useAsyncResource } from '../hooks/useAsyncResource';
import { getStarGridHealth, getStarGridInfra, listPnlReports } from '../services/starGridService';
import { PnlReportSummary, StarGridHealth, StarGridInfra } from '../starGridTypes';

/** StarGrid's shared server state, so views depend on this contract rather than on how it is fetched. */
export interface StarGridData {
  /** Persisted reports, shared by the Reports nav section and the report views. */
  pnlReports: AsyncResource<PnlReportSummary[]>;
  infra: AsyncResource<StarGridInfra>;
  health: AsyncResource<StarGridHealth>;
}

const StarGridDataContext = createContext<StarGridData | undefined>(undefined);

/** Shared display state; each view entry requests fresh data through stable load callbacks. */
export const StarGridDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pnlReports = useAsyncResource<PnlReportSummary[]>(listPnlReports);
  const infra = useAsyncResource<StarGridInfra>(getStarGridInfra);
  const health = useAsyncResource<StarGridHealth>(getStarGridHealth);

  const value = useMemo<StarGridData>(() => ({ pnlReports, infra, health }), [pnlReports, infra, health]);

  return <StarGridDataContext.Provider value={value}>{children}</StarGridDataContext.Provider>;
};

/** Access StarGrid's shared server state. Must be called within a StarGridDataProvider. */
export function useStarGridData(): StarGridData {
  const context = useContext(StarGridDataContext);
  if (!context) {
    throw new Error('useStarGridData must be used within a StarGridDataProvider.');
  }
  return context;
}
